import { notify, type GoldStandardResult, clientConfig } from "@quickpoint.me/shared";
import { useEffect, useRef, useState } from "react";
import { fireClearCodeEvent, fireUpdateCodeEvent } from "../lib/editor/events";

import { toPng } from "html-to-image";
import { useRecoilValue } from "recoil";
import { layoutExamples } from "../examples";
import { FilterNamesSelector } from "../state/filters";

const TEST_MODE = true;

/**
 * Wait until every image in the frame has actually loaded — both <img>
 * elements and CSS background-images (the `background:` keyword). A fixed
 * delay raced the network and captured slides with missing images.
 */
const waitForImages = async (frame: HTMLElement, timeoutMs = 15000) => {
	const deadline = Date.now() + timeoutMs;

	// preload every CSS background-image url so the browser has it cached
	const bgUrls = new Set<string>();
	for (const el of frame.querySelectorAll<HTMLElement>("*")) {
		const bg = getComputedStyle(el).backgroundImage;
		const match = bg.match(/url\("?([^")]+)"?\)/);
		if (match) bgUrls.add(match[1]);
	}
	await Promise.all(
		[...bgUrls].map(
			(url) =>
				new Promise<void>((resolve) => {
					const img = new Image();
					img.onload = () => resolve();
					img.onerror = () => resolve();
					img.src = url;
					setTimeout(resolve, Math.max(0, deadline - Date.now()));
				}),
		),
	);

	// poll <img> elements until complete (new ones can appear as grids reweigh)
	for (;;) {
		const pending = [...frame.querySelectorAll("img")].filter(
			(img) => !(img.complete && img.naturalWidth > 0),
		);
		if (pending.length === 0) break;
		if (Date.now() > deadline) {
			console.warn(`waitForImages: timed out with ${pending.length} pending`);
			break;
		}
		await new Promise((r) => setTimeout(r, 250));
	}

	// settle: grid reweighs on image load (aspect-ratio driven), give it a beat
	await new Promise((r) => setTimeout(r, 500));
};

const GoldStandardGenerator = () => {
	// dev tool: available in local dev and staging, never in production
	// (process.env.NODE_ENV is how clientConfig already distinguishes envs)
	if (process.env.NODE_ENV === "production") return null;

	// collapsed by default so it doesn't sit on top of the app; open with the
	// launcher handle or Ctrl/Cmd+Shift+G
	const [open, setOpen] = useState(false);
	const [generating, setGenerating] = useState(false);
	const [viewing, setViewing] = useState(false);
	const [selectedFilter, setSelectedFilter] = useState("");
	const [gallery, setGallery] = useState<GoldStandardResult[]>([]);
	const [currentIndex, setCurrentIndex] = useState(0);
	const filterNames = useRecoilValue(FilterNamesSelector);
	const abortRef = useRef(false);

	// keyboard shortcut to toggle the panel (Ctrl/Cmd+Shift+G); Esc closes
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === "g") {
				e.preventDefault();
				setOpen((v) => !v);
			} else if (e.key === "Escape" && open && !viewing) {
				setOpen(false);
			}
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [open, viewing]);

	useEffect(() => {
		if (viewing && selectedFilter) {
			const fetchGallery = async () => {
				try {
					const url = `${clientConfig.layoutAi()}/gold-standards?filter=${encodeURIComponent(selectedFilter)}`;
					const res = await fetch(url);
					const data = await res.json();
					if (Array.isArray(data)) {
						setGallery(data);
						setCurrentIndex(0);
					}
				} catch (e) {
					console.error(e);
				}
			};
			fetchGallery();
		}
	}, [viewing, selectedFilter]);

	const handleNext = () => {
		setCurrentIndex((prev) => (prev + 1) % gallery.length);
	};

	const handlePrev = () => {
		setCurrentIndex((prev) => (prev - 1 + gallery.length) % gallery.length);
	};

	const toggleGeneration = async () => {
		if (generating) {
			abortRef.current = true;
			return;
		}

		setGenerating(true);
		abortRef.current = false;
		const results: Record<string, GoldStandardResult[]> = {};

		const filtersToRun = TEST_MODE ? ["quickpoint::default"] : filterNames;
		const examplesToRun = layoutExamples;

		console.log("Starting Generation. Filters found:", filtersToRun.length);

		for (const filterName of filtersToRun) {
			if (abortRef.current) break;
			console.log(`Processing Filter: ${filterName}`);
			results[filterName] = [];

			for (const example of examplesToRun) {
				if (abortRef.current) break;
				console.log(`  - Layout: ${example.name}`);

				// 1. Clear and Inject
				fireClearCodeEvent();
				await new Promise((r) => setTimeout(r, 100)); // Debounce clear

				// Prepend filter name to Jadle
				const fullJadle = `${TEST_MODE ? "" : `filter: ${filterName}\n`}${example.jadle}`;
				fireUpdateCodeEvent(fullJadle);

				// 2. Wait for Render & Filter Load
				await new Promise((r) => setTimeout(r, 3000));

				// 3. Capture
				const frame = document.querySelector(
					"#landscape-preview-pane",
				) as HTMLElement;
				if (frame) {
					await waitForImages(frame);
					try {
						// Apply text stability styles before capture
						const textElements = frame.querySelectorAll("*");
						const originalStyles: {
							el: HTMLElement;
							whiteSpace: string;
							wordBreak: string;
						}[] = [];

						for (const el of textElements) {
							const htmlEl = el as HTMLElement;
							originalStyles.push({
								el: htmlEl,
								whiteSpace: htmlEl.style.whiteSpace,
								wordBreak: htmlEl.style.wordBreak,
							});
							htmlEl.style.whiteSpace = "pre-wrap";
							htmlEl.style.wordBreak = "keep-all";
						}

						// html-to-image rasterizes its SVG before freshly-embedded
						// images finish decoding (first use of each image comes out
						// blank). Warm-up pass forces the decode; second pass is the
						// keeper. cacheBust off so fetches hit the HTTP cache.
						const captureOptions = {
							cacheBust: false,
							pixelRatio: 1,
							canvasHeight: 1000,
							canvasWidth: 1680,
						};
						await toPng(frame, captureOptions);
						const dataUrl = await toPng(frame, captureOptions);

						const sizeInBytes = (dataUrl.length * 3) / 4;
						const sizeInMB = sizeInBytes / (1024 * 1024);
						console.log(`Captured Image Size: ${sizeInMB.toFixed(2)} MB`);

						// Restore original styles
						for (const { el, whiteSpace, wordBreak } of originalStyles) {
							el.style.whiteSpace = whiteSpace;
							el.style.wordBreak = wordBreak;
						}

						results[filterName].push({
							filter: filterName,
							layout: example.name,
							jadle: example.jadle,
							image: dataUrl,
						});
					} catch (e) {
						console.error(
							`Failed to capture ${example.name} on ${filterName}`,
							e,
						);
					}
				} else {
					console.error("Preview frame not found!");
				}
			}
		}

		// 4. Save to KV
		const totalItems = Object.values(results).flat().length;
		if (!abortRef.current && totalItems > 0) {
			try {
				const saveUrl = `${clientConfig.layoutAi()}/save-gold-standards`;
				console.log("Saving to KV:", saveUrl, results);
				const response = await fetch(saveUrl, {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify(results),
				});
				if (response.ok) {
					notify(
						`Success! Saved ${totalItems} Gold Standard examples to System KV.`,
					);
				} else {
					notify(`Failed to save to KV: ${response.statusText}`);
				}
			} catch (e) {
				console.error(e);
				notify("Error saving to KV");
			}
		} else if (abortRef.current) {
			notify("Generation Stopped.", { variant: "info" });
		}

		setGenerating(false);
	};

	if (viewing) {
		const currentItem = gallery[currentIndex];
		return (
			<div
				style={{
					position: "fixed",
					top: 0,
					left: 0,
					width: "100%",
					height: "100%",
					background: "white",
					zIndex: 10000,
					overflow: "hidden",
					padding: 20,
					display: "flex",
					flexDirection: "column",
				}}
			>
				<div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
					<button type="button" onClick={() => setViewing(false)}>
						Close Gallery
					</button>
					<select
						onChange={(e) => setSelectedFilter(e.target.value)}
						value={selectedFilter}
					>
						<option value="">Select Filter...</option>
						{filterNames.map((f) => (
							<option key={f} value={f}>
								{f}
							</option>
						))}
					</select>
				</div>

				{gallery.length > 0 && currentItem ? (
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							flex: 1,
							overflow: "hidden",
						}}
					>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								width: "100%",
								alignItems: "center",
								padding: "10px 0",
							}}
						>
							<button
								type="button"
								onClick={handlePrev}
								disabled={gallery.length <= 1}
								style={{ fontSize: "1.5rem", padding: "10px 20px" }}
							>
								&lt;
							</button>
							<div style={{ textAlign: "center" }}>
								<h3 style={{ margin: 0 }}>{currentItem.layout}</h3>
								<small>
									{currentIndex + 1} / {gallery.length}
								</small>
							</div>
							<button
								type="button"
								onClick={handleNext}
								disabled={gallery.length <= 1}
								style={{ fontSize: "1.5rem", padding: "10px 20px" }}
							>
								&gt;
							</button>
						</div>

						<div
							style={{
								flex: 1,
								width: "100%",
								display: "flex",
								justifyContent: "center",
								overflow: "hidden",
								padding: 10,
								background: "#f5f5f5",
							}}
						>
							<img
								src={currentItem.image}
								style={{
									maxWidth: "100%",
									maxHeight: "100%",
									objectFit: "contain",
									border: "1px solid #ddd",
									background: "white",
								}}
								alt={currentItem.layout}
							/>
						</div>
						<pre
							style={{
								maxHeight: 100,
								overflow: "auto",
								width: "100%",
								background: "#eee",
								padding: 10,
								margin: 0,
								fontSize: 12,
							}}
						>
							{currentItem.jadle}
						</pre>
					</div>
				) : (
					<p>
						{selectedFilter
							? "No images found for this filter."
							: "Select a filter to view gold standards."}
					</p>
				)}
			</div>
		);
	}

	// collapsed: fully hidden — Ctrl/Cmd+Shift+G is the only way in, so the
	// dev tool never sits on top of the app chrome (it covered the portrait
	// More button)
	if (!open) {
		return null;
	}

	return (
		<div
			style={{
				position: "fixed",
				bottom: 10,
				right: 10,
				zIndex: 9999,
				display: "flex",
				flexDirection: "column",
				gap: 8,
				padding: 12,
				background: "white",
				border: "1px solid #ccc",
				borderRadius: 8,
				boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
				fontFamily: "system-ui, sans-serif",
			}}
		>
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					gap: 12,
				}}
			>
				<strong style={{ fontSize: 13 }}>Gold Standards (dev)</strong>
				<button
					type="button"
					onClick={() => setOpen(false)}
					aria-label="Close"
					style={{
						background: "none",
						border: "none",
						fontSize: 16,
						cursor: "pointer",
						color: "#666",
					}}
				>
					✕
				</button>
			</div>
			<button
				type="button"
				onClick={toggleGeneration}
				style={{
					padding: "8px 10px",
					background: generating ? "orange" : "#c0392b",
					color: "white",
					border: "none",
					borderRadius: 6,
					cursor: "pointer",
				}}
			>
				{generating ? "Stop Generation" : "Generate Gold Standards"}
			</button>
			<button
				type="button"
				onClick={() => setViewing(true)}
				style={{
					padding: "8px 10px",
					background: "#333",
					color: "white",
					border: "none",
					borderRadius: 6,
					cursor: "pointer",
				}}
			>
				Review Gallery
			</button>
		</div>
	);
};

export default GoldStandardGenerator;
